import { mappingKinds, type CodelistEntry, type CodelistEntryInput, type MappingKind } from '../../api/types';

export interface ParsedCodelistJson {
  /** The kind the pasted file names, when it names a known one; the page decides what to do with it. */
  kind: MappingKind | null;
  versionLabel: string | null;
  sourceNote: string | null;
  entries: CodelistEntryInput[];
}

export type ParseFailureKey =
  | 'codelists.versions.json.invalid'
  | 'codelists.versions.json.noEntries'
  | 'codelists.versions.json.blankEntry'
  | 'codelists.versions.json.duplicateCode';

export type ParseResult =
  { ok: true; value: ParsedCodelistJson } | { ok: false; reasonKey: ParseFailureKey; detail?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function knownKind(value: unknown): MappingKind | null {
  return typeof value === 'string' && (mappingKinds as readonly string[]).includes(value)
    ? (value as MappingKind)
    : null;
}

/**
 * Reads what an administrator pastes: either a whole import file (the shape of data/codelists/*.json)
 * or just the entries array. Only the shape is checked here — statuses, one-draft-per-kind and label
 * uniqueness are the server's to decide, and its answer is shown as it comes.
 */
export function parseCodelistJson(text: string): ParseResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { ok: false, reasonKey: 'codelists.versions.json.invalid' };
  }

  const file = isRecord(parsed) ? parsed : null;
  const rawEntries = file ? file.entries : parsed;
  if (!Array.isArray(rawEntries) || rawEntries.length === 0) {
    return { ok: false, reasonKey: 'codelists.versions.json.noEntries' };
  }

  const entries: CodelistEntryInput[] = [];
  const seen = new Set<string>();
  for (const raw of rawEntries) {
    if (!isRecord(raw)) return { ok: false, reasonKey: 'codelists.versions.json.blankEntry' };

    const code = stringOrNull(raw.code);
    const name = stringOrNull(raw.name);
    if (code === null || name === null) return { ok: false, reasonKey: 'codelists.versions.json.blankEntry' };
    if (seen.has(code))
      return { ok: false, reasonKey: 'codelists.versions.json.duplicateCode', detail: code };

    seen.add(code);
    entries.push({ code, name, description: stringOrNull(raw.description) });
  }

  return {
    ok: true,
    value: {
      kind: file ? knownKind(file.kind) : null,
      versionLabel: file ? stringOrNull(file.versionLabel) : null,
      sourceNote: file ? stringOrNull(file.sourceNote) : null,
      entries,
    },
  };
}

/** The entries of a version as an editable document — statuses left out, the server recomputes them. */
export function entriesToJson(entries: readonly CodelistEntry[]): string {
  return JSON.stringify(
    entries.map(({ code, name, description }) => ({ code, name, description })),
    null,
    2,
  );
}
